import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {List} from 'immutable';

import type {
  AssignmentModelV2,
  ClassroomInvitationModelV1,
  ClassroomModelV1,
  SubjectModelV2,
  StudentModelV2
} from '@albert-io/models';
import {addToast} from '@albert-io/atomic';
import errorCodes from 'client/errorCodes';
import {ERROR_TYPES} from 'client/constants';
import {genericMandarkRequest} from 'resources/mandark.resource';
import notifier from '@albert-io/notifier';
import {query} from '@albert-io/json-api-framework/request/builder';
import {replaceQueryParams} from 'client/history';

import {
  getClassroomInvitationsQuery as getInvitationsQuery,
  getClassroomsQuery,
  getIndiePracticeSubjectsQuery as getSubjectsQuery,
  getStudentQuery
} from './StudentDashboard.queries';

type State = {
  areClassroomsLoading: boolean;
  areIndependentPracticeSubjectsLoading: boolean;
  areInvitationsLoading: boolean;
  assignmentsLastFetchedAt: number;
  classroomCode: string;
  classroomInvitations: List<ClassroomInvitationModelV1> | null;
  classrooms: List<ClassroomModelV1>;
  duplicateClassroom: ClassroomModelV1 | null;
  exceededHardCapLimit: boolean;
  independentPracticeSubjectsLastFetchedAt: number;
  independentPracticeSubjects: SubjectModelV2[];
  invitations: List<ClassroomInvitationModelV1>;
  isReady: boolean;
  isSidebarExpanded: boolean;
  isStudentEnrollmentError: boolean;
  isStudentEnrollmentPending: boolean;
  openAssignments: List<AssignmentModelV2>;
  page: number;
  pastDueAssignments: List<AssignmentModelV2>;
  showJoinClassModal: boolean;
  skillPathLastFetchedAt: number;
  student: StudentModelV2 | null;
  studentEmail: string | null;
  studentHasInvalidEmail: boolean;
  studentLastFetchedAt: number;
  subjects: List<SubjectModelV2>;
  totalPages: number;
  userDefaultRoute: string | null;
};

type Payload = {
  onReject: (err?: any) => void;
  onResolve: () => void;
  relationship: any;
};

type Actions = {
  bootstrapStudentDashboard: (pageOverride?: number, classroomId?: string) => void;
  clearDashboardCache: (page: number) => void;
  enrollStudent: (payload: Payload) => Promise<void>;
  setAreClassroomsLoading: (areClassroomsLoading: State['areClassroomsLoading']) => void;
  setAreIndependentPracticeSubjectsLoading: (
    areIndependentPracticeSubjectsLoading: State['areIndependentPracticeSubjectsLoading']
  ) => void;
  setAreInvitationsLoading: (areInvitationsLoading: State['areInvitationsLoading']) => void;
  setClassroomCode: (classroomCode: State['classroomCode']) => void;
  setClassrooms: (classrooms: List<ClassroomModelV1>) => void;
  setDuplicateClassroom: (duplicateClassroom: State['duplicateClassroom']) => void;
  setExceededHardCapLimit: (hardCapExceeded: State['exceededHardCapLimit']) => void;
  setIndependentPracticeSubjects: (
    independentPracticeSubjects: State['independentPracticeSubjects']
  ) => void;
  setInvitations: (invitations: State['invitations']) => void;
  setIsSidebarExpanded: (isSidebarExpanded: State['isSidebarExpanded']) => void;
  setIsStudentEnrollmentError: (
    isStudentEnrollmentError: State['isStudentEnrollmentError']
  ) => void;
  setIsStudentEnrollmentPending: (
    isStudentEnrollmentPending: State['isStudentEnrollmentPending']
  ) => void;
  setOpenAssignments: (openAssignments: State['openAssignments']) => void;
  setPage: (page: State['page']) => void;
  setPastDueAssignments: (pastDueAssignments: State['pastDueAssignments']) => void;
  setShowJoinClassModal: (showJoinClassModal: State['showJoinClassModal']) => void;
  setStudent: (student: StudentModelV2) => void;
  setStudentEmail: (studentEmail: NonNullable<State['studentEmail']>) => void;
  setStudentHasInvalidEmail: (hasInvalidEmail: State['studentHasInvalidEmail']) => void;
  setUserDefaultRoute: (userDefaultRoute: State['userDefaultRoute']) => void;
  updateAssignmentsLastFetchedAt: () => void;
  updateIndependentPracticeSubjectsLastFetchedAt: () => void;
  updateSkillPathLastFetchedAt: () => void;
};

const initialState: State = {
  areClassroomsLoading: false,
  areIndependentPracticeSubjectsLoading: false,
  areInvitationsLoading: false,
  assignmentsLastFetchedAt: 0,
  classroomCode: '',
  classroomInvitations: null,
  classrooms: List([]),
  duplicateClassroom: null,
  exceededHardCapLimit: false,
  independentPracticeSubjects: [],
  independentPracticeSubjectsLastFetchedAt: 0,
  invitations: List([]),
  isReady: false,
  isSidebarExpanded: false,
  isStudentEnrollmentError: false,
  isStudentEnrollmentPending: false,
  openAssignments: List([]),
  page: 1,
  pastDueAssignments: List([]),
  showJoinClassModal: false,
  skillPathLastFetchedAt: 0,
  student: null,
  studentEmail: null,
  studentHasInvalidEmail: false,
  studentLastFetchedAt: 0,
  subjects: List([]),
  totalPages: 1,
  userDefaultRoute: null
};

const PAGE_SIZE = 6;

export const useStudentDashboard = create<State & Actions>()(
  devtools(
    immer((set, get) => ({
      ...initialState,
      bootstrapStudentDashboard: async (page, classroomId) => {
        const shouldEnrollStudentByParam = Boolean(classroomId);
        try {
          set((state) => {
            state.isReady = false;
          });
          const fetchedStudent = await getStudentQuery().getResourcePromise();
          const studentId = fetchedStudent.getId();
          const email = fetchedStudent.getEmail();

          const paginatedClassroomsQuery = getClassroomsQuery(studentId)
            .page(page)
            .pageSize(PAGE_SIZE);
          const fetchedClassrooms = await paginatedClassroomsQuery.getResourcePromise();

          const fetchedInvitations = await getInvitationsQuery(email).getResourcePromise();
          // @todo Refactor getClassroomInvitationsQuery with the query params that will achieve this same behavior
          const filteredInvitations = fetchedInvitations.filter(
            (invitation) =>
              !invitation.getClassroom().isEmpty() && !invitation.getIssuer().isEmpty()
          );

          const fetchedSubjects = await getSubjectsQuery().getResourcePromise();

          const assignmentInfo = fetchedStudent?.getMeta?.()?.getAssignmentInfo?.();
          const fetchedOpenAssignments = assignmentInfo?.get?.('open_assignments');
          const fetchedPastDueAssignments = assignmentInfo?.get?.('past_due_assignments');
          const totalPages = paginatedClassroomsQuery
            .getResourceMetadata()
            .getIn(['page', 'total_pages'], 1);

          set((state) => {
            state.openAssignments = fetchedOpenAssignments;
            state.pastDueAssignments = fetchedPastDueAssignments;
            state.classrooms = fetchedClassrooms;
            state.invitations = filteredInvitations;
            state.student = fetchedStudent;
            state.subjects = fetchedSubjects;
            state.totalPages = totalPages;
            state.isReady = shouldEnrollStudentByParam ? false : true;
          });
        } catch (_err) {
          addToast({
            title: 'Something went wrong',
            color: 'negative',
            message:
              'There was a problem loading your classrooms. If the problem persists, contact us.'
          });
          history.pushState(null, `/error?errorCode=${ERROR_TYPES.CRITICAL}`);
        }

        if (shouldEnrollStudentByParam) {
          const {enrollStudent, bootstrapStudentDashboard} = get();
          const relationship = {
            id: classroomId,
            type: 'classroom_v1'
          };
          await enrollStudent({
            onReject: () => {
              set((state) => {
                state.isReady = true;
              });
              addToast({
                title: 'Something went wrong',
                color: 'negative',
                message:
                  'We were unable to complete your enrollment. Please verify the invitation with your teacher and try again.'
              });
            },
            onResolve: () => {
              replaceQueryParams({classroomId: null, classroomInvitationId: null});
              // replicates old behavior of reloading entire dashboard. // https://github.com/albert-io/nexus/issues/13443#issuecomment-3374784269
              bootstrapStudentDashboard(page);
            },
            relationship
          });
        }
      },
      clearDashboardCache: (page: number) => {
        const {student} = get();
        if (student) {
          const id = student.getId();
          const email = student.getEmail();
          getInvitationsQuery(email).invalidateInterest();
          getClassroomsQuery(id).page(page).pageSize(PAGE_SIZE).invalidateInterest();
          getSubjectsQuery().invalidateInterest();
          getStudentQuery().invalidateInterest();
        }
      },
      enrollStudent: async ({onReject = () => {}, onResolve = () => {}, relationship = {}}) => {
        const {
          page,
          setExceededHardCapLimit,
          setIsStudentEnrollmentError,
          setIsStudentEnrollmentPending,
          setStudentHasInvalidEmail,
          student
        } = get();
        if (student === null) {
          return;
        }

        setIsStudentEnrollmentPending(true);

        genericMandarkRequest(
          'post',
          query()
            .mandarkEndpoint(['students_v2', student.getId(), 'relationships', 'classrooms_v1'])
            .done(),
          {
            data: [relationship]
          }
        )
          .then(() => {
            addToast({
              duration: 10000,
              message: 'You have joined a new class',
              title: 'Success!',
              color: 'positive'
            });

            onResolve();

            set((state) => {
              state.isReady = true;
            });
          })
          .catch((error) => {
            setIsStudentEnrollmentError(true);

            const exceededHardCapLimitError =
              error.response?.status === 403 &&
              error.response?.body.errors[0].code === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT;

            if (exceededHardCapLimitError) {
              setExceededHardCapLimit(true);
            }

            // don't notify for 400 errors (to suppress invalid join code)
            if (error.response?.status !== 400) {
              notifier.notify(error, {
                message: 'Failed to enroll student using relationship',
                tags: 'StudentDashboardProvider',
                context: {
                  relationship: relationship
                }
              });
            }

            // Catch the Errors.BadRequest.InvalidEmailDomain here to cover join code, email link, and invitation card enrollment flows
            const invalidEmail =
              error.response?.status === 400 &&
              error.response?.body.errors[0].code ===
                errorCodes.BAD_REQUEST.VALIDATION_ERROR.INVALID_EMAIL_DOMAIN;

            if (invalidEmail) {
              setStudentHasInvalidEmail(true);
            }

            onReject(error);
          })
          .finally(() => {
            setIsStudentEnrollmentPending(false);
          });
      },
      setAreClassroomsLoading: (areClassroomsLoading: State['areClassroomsLoading']) => {
        set((state) => {
          state.areClassroomsLoading = areClassroomsLoading;
        });
      },
      setAreIndependentPracticeSubjectsLoading(areIndependentPracticeSubjectsLoading) {
        set((state) => {
          state.areIndependentPracticeSubjectsLoading = areIndependentPracticeSubjectsLoading;
        });
      },
      setAreInvitationsLoading: (areInvitationsLoading: State['areInvitationsLoading']) => {
        set((state) => {
          state.areInvitationsLoading = areInvitationsLoading;
        });
      },
      setClassroomCode(classroomCode) {
        set((state) => {
          state.classroomCode = classroomCode;
        });
      },
      setClassrooms: (classrooms: State['classrooms']) => {
        set((state) => {
          state.classrooms = classrooms;
        });
      },
      setDuplicateClassroom: (duplicateClassroom: State['duplicateClassroom']) => {
        set((state) => {
          state.duplicateClassroom = duplicateClassroom;
        });
      },
      setExceededHardCapLimit: (hardCapExceeded: State['exceededHardCapLimit']) => {
        set((state) => {
          state.exceededHardCapLimit = hardCapExceeded;
        });
      },
      setIndependentPracticeSubjects(independentPracticeSubjects) {
        set((state) => {
          state.independentPracticeSubjects = independentPracticeSubjects;
        });
      },
      setInvitations: (invitations: State['invitations']) => {
        set((state) => {
          state.invitations = invitations;
        });
      },
      setIsSidebarExpanded: (isSidebarExpanded: State['isSidebarExpanded']) => {
        set((state) => {
          state.isSidebarExpanded = isSidebarExpanded;
        });
      },
      setIsStudentEnrollmentError(isStudentEnrollmentError) {
        set((state) => {
          state.isStudentEnrollmentError = isStudentEnrollmentError;
        });
      },
      setIsStudentEnrollmentPending(isStudentEnrollmentPending) {
        set((state) => {
          state.isStudentEnrollmentPending = isStudentEnrollmentPending;
        });
      },
      setOpenAssignments: (openAssignments: State['openAssignments']) => {
        set((state) => {
          state.openAssignments = openAssignments;
        });
      },
      setPage: (page: State['page']) => {
        set((state) => {
          state.page = page;
        });
      },
      setPastDueAssignments: (pastDueAssignments: State['pastDueAssignments']) => {
        set((state) => {
          state.pastDueAssignments = pastDueAssignments;
        });
      },
      setShowJoinClassModal: (showJoinClassModal: State['showJoinClassModal']) => {
        set((state) => {
          state.classroomCode = '';
          state.isStudentEnrollmentError = false;
          state.isStudentEnrollmentPending = false;
          state.showJoinClassModal = showJoinClassModal;
        });
      },
      setStudent: (student: State['student']) => {
        set((state) => {
          state.student = student;
        });
      },
      setStudentEmail(studentEmail) {
        set((state) => {
          state.studentEmail = studentEmail;
        });
      },
      setStudentHasInvalidEmail: (hasInvalidEmail: State['studentHasInvalidEmail']) => {
        set((state) => {
          state.studentHasInvalidEmail = hasInvalidEmail;
        });
      },
      setUserDefaultRoute(userDefaultRoute) {
        set((state) => {
          state.userDefaultRoute = userDefaultRoute;
        });
      },
      updateAssignmentsLastFetchedAt: () => {
        set((state) => {
          state.assignmentsLastFetchedAt = Date.now();
        });
      },
      updateIndependentPracticeSubjectsLastFetchedAt: () => {
        set((state) => {
          state.independentPracticeSubjectsLastFetchedAt = Date.now();
        });
      },
      updateSkillPathLastFetchedAt: () => {
        set((state) => {
          state.skillPathLastFetchedAt = Date.now();
        });
      }
    }))
  )
);
